import { useMemo } from "react";
import { Language } from "../../../../components/common/ApiReferenceDialog/LanguagesDropdown/LanguagesDropdown";
import { codeGenerators } from "../../../../components/common/ApiReferenceDialog/utils/codeGenerators";
import { VideoEditingConfigType, VideoEditingTools } from "../../../../models/video-editing";

const getVideoEditingEndpointUrl = (tool: VideoEditingTools): string => {
	switch (tool) {
		case "erase":
			return "https://engine.prod.bria-api.com/v2/video/edit/erase";
		case "remove_background":
			return "https://engine.prod.bria-api.com/v2/video/edit/remove_background";
		case "solid_background":
			return "https://engine.prod.bria-api.com/v2/video/edit/remove_background";
		case "increase_resolution":
			return "https://engine.prod.bria-api.com/v2/video/edit/increase_resolution";
		default:
			return "https://engine.prod.bria-api.com/v2/video/edit/remove_background";
	}
};

const generateVideoEditingPayload = (tool: VideoEditingTools, config: VideoEditingConfigType): Record<string, any> => {
	const video = config.video || "https://example.com/example.mp4";
	const mask = config.tool_config?.erase?.mask || "required";
	const backgroundColor = config.tool_config?.solid_background?.background_color || "required";
	const desiredIncrease = config.tool_config?.increase_resolution?.desired_increase || "required";

	switch (tool) {
		case "erase":
			return { video, mask };
		case "remove_background":
			return { video };
		case "solid_background":
			return { video, background_color: backgroundColor };
		case "increase_resolution":
			return { video, desired_increase: desiredIncrease };
		default:
			return { video };
	}
};

const generateVideoEditingCurlCode = (payload: Record<string, any>, url: string, token: string): string => {
	const jsonBody = JSON.stringify(payload, null, 2);
	const safeBody = jsonBody.replace(/'/g, "'\"'\"'");
	return `curl -i -X POST \\
  ${url} \\
  -H 'Content-Type: application/json' \\
  -H 'api_token: ${token}' \\
  -d '${safeBody}'`;
};

export const useVideoEditingPayload = (
	tool: VideoEditingTools | undefined,
	language: Language,
	config: VideoEditingConfigType,
) => {
	const rawPayload = useMemo(() => {
		if (!tool) return null;
		return generateVideoEditingPayload(tool, config);
	}, [
		tool,
		config.video,
		config.tool_config?.erase?.mask,
		config.tool_config?.solid_background?.background_color,
		config.tool_config?.increase_resolution?.desired_increase,
	]);

	const rawJsonPayload = useMemo(() => {
		if (!rawPayload) return null;
		return JSON.stringify(rawPayload, null, 2);
	}, [rawPayload]);

	const fullRequestCode = useMemo(() => {
		if (!tool || !rawPayload) return null;
		const url = getVideoEditingEndpointUrl(tool);
		const apiToken = "YOUR_API_TOKEN";
		const payload = rawPayload;

		if (language === "curl") {
			return generateVideoEditingCurlCode(payload, url, apiToken);
		}

		const generator = codeGenerators[language];
		return generator ? generator(payload, url, apiToken) : "";
	}, [tool, language, rawPayload]);

	return {
		payload: rawPayload,
		rawJsonPayload,
		fullRequestCode,
	};
};
